define("shared/helpers/linkify-text", ["exports", "@ember/component/helper", "@ember/template", "dompurify", "shared/utils/escape-html"], function (_exports, _helper, _template, _dompurify, _escapeHtml) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.default = void 0;
  0; //eaimeta@70e063a35619d71f0,"@ember/component/helper",0,"@ember/template",0,"dompurify",0,"shared/utils/escape-html"eaimeta@70e063a35619d71f
  // Matches an http(s):// URL or a bare `www.` host. /i covers `HTTP://` and `WWW.`, and /g
  // lets `exec()` walk the string. The inner lookahead stops a match at the next scheme so
  // `https://a.com/x,https://b.com` (two links pasted with no space) doesn't swallow the
  // second into the first and emit one dead anchor. Deliberately still allows commas, so
  // URLs that legitimately contain them (map coordinates) survive intact.
  const URL_REGEX = /(?:https?:\/\/|www\.)(?:(?!https?:\/\/)\S)+/gi;

  // Punctuation and quotes that commonly trail URLs in prose but aren't part of the URL,
  // e.g. the period in "Check out https://later.com." Quotes matter more than they look: a
  // trailing one lands in the *hostname*, so `https://later.com"` resolves to the host
  // `later.com"` and the link is simply dead.
  const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)\]}>'"`”’»]+$/;
  const HAS_SCHEME_REGEX = /^https?:\/\//i;

  // Characters that mean the match landed inside a larger token rather than at the start of
  // a URL: `foo-http://evil.com`, `ftp://www.x`, `brad@www.x`, `shot-www.png`, `#www.x`.
  // `:` is deliberately absent so `Link:https://later.com` keeps working.
  // Deliberately not a lookbehind, which is a parse-time syntax error on older Safari.
  const MID_TOKEN_CHAR_REGEX = /[\w@./\-+~%#]/;

  // Hosts are letters, digits, dots and hyphens (IDN arrives here already punycoded).
  // Anything else means we mis-sliced, e.g. `www.a.com,www.b.com` parsing as one host.
  const VALID_HOSTNAME_REGEX = /^[a-z0-9.-]+$/i;
  // Resolves a matched substring to an href, or undefined when it isn't a usable http(s) URL.
  // A bare `www.` match gets https:// so the browser doesn't read the href as a relative path.
  const toHref = candidate => {
    const hasScheme = HAS_SCHEME_REGEX.test(candidate);
    const withScheme = hasScheme ? candidate : `https://${candidate}`;
    try {
      const parsed = new URL(withScheme);

      // `www.later.com@evil.com` parses as userinfo plus host evil.com, so the text reads as
      // one domain while the link goes to another. Refuse it rather than render the anchor.
      if (parsed.username || parsed.password) {
        return undefined;
      }
      if (!VALID_HOSTNAME_REGEX.test(parsed.hostname)) {
        return undefined;
      }

      // A host we guessed the scheme for has to at least look like a domain. Drops the
      // `https://www` left behind when trailing punctuation is peeled off `www.,`
      if (!hasScheme && !parsed.hostname.includes('.')) {
        return undefined;
      }
      return withScheme;
    } catch {
      return undefined;
    }
  };

  /**
   * Renders URLs inside a plain-text string as anchors that open in a new tab. The link text
   * is whatever was typed; only the href gets a scheme filled in. Mirrors the rules of Later
   * Influence's `linkifyText` util so both sides of a concept submission behave the same way.
   *
   * The input is escaped before the anchors are spliced in, and the result runs through
   * DOMPurify, because the return value is `htmlSafe` and the text is user-authored.
   *
   * Approach:
   *   for each URL the regex finds in the input:
   *     1. peel off any trailing punctuation or quotes
   *     2. skip it if the match started mid-token, so it isn't really the start of a URL
   *     3. if what's left resolves to an http(s) href, emit: [escaped prior text, <a>, trailing]
   *        else emit the match as escaped plain text
   *   then, emit whatever text remains after the last match
   */
  const linkifyTextHelperFn = _ref => {
    let [text] = _ref;
    const source = text ?? '';
    let html = '';
    let lastIndex = 0; // end of the most recently consumed slice of `source`

    // Reset the regex's internal cursor — /g regexes are stateful across exec() calls
    URL_REGEX.lastIndex = 0;
    let match = URL_REGEX.exec(source);
    while (match !== null) {
      const [fullMatch] = match;
      const startIndex = match.index;
      const trailing = TRAILING_PUNCTUATION_REGEX.exec(fullMatch)?.[0] ?? '';
      const candidate = trailing ? fullMatch.slice(0, fullMatch.length - trailing.length) : fullMatch;
      html += (0, _escapeHtml.default)(source.slice(lastIndex, startIndex));
      const atTokenStart = startIndex === 0 || !MID_TOKEN_CHAR_REGEX.test(source[startIndex - 1] ?? '');
      const href = atTokenStart ? toHref(candidate) : undefined;
      if (href) {
        const attrs = `href="${(0, _escapeHtml.default)(href)}" rel="noopener noreferrer" target="_blank"`;
        html += `<a ${attrs}>${(0, _escapeHtml.default)(candidate)}</a>${(0, _escapeHtml.default)(trailing)}`;
      } else {
        // Regex matched something the URL parser rejected, so render verbatim
        html += (0, _escapeHtml.default)(fullMatch);
      }
      lastIndex = startIndex + fullMatch.length;
      match = URL_REGEX.exec(source);
    }

    // Tail: any text after the final URL match (or all of `source` if no matches)
    html += (0, _escapeHtml.default)(source.slice(lastIndex));
    const sanitizedHtml = _dompurify.default.sanitize(html, {
      ALLOWED_ATTR: ['href', 'rel', 'target'],
      ALLOWED_TAGS: ['a']
    });
    return (0, _template.htmlSafe)(sanitizedHtml);
  };
  const linkifyTextHelper = (0, _helper.helper)(linkifyTextHelperFn);
  var _default = _exports.default = linkifyTextHelper;
});